/**
 * useFollowUpCount Hook
 *
 * Returns the count of overdue + today follow-ups for the nav badge.
 * Uses a dedicated Firestore query filtered to quotes with a followUpDate set,
 * so the count is not limited by the 20-quote page size of the general useQuotes hook.
 *
 * @param enabled - Only fetch quotes when true (avoids unnecessary queries outside quoting module)
 */

import { useQuery } from '@tanstack/react-query';
import { collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { COLLECTIONS } from '../../config/collections';
import { useOrganization } from '../../contexts/OrganizationContext';
import type { Quote, QuoteStatus } from '../../types/firestore';

// Every status except Approved/Rejected — Firestore's `in` operator is
// equality-type and CAN be combined with a range filter on a different
// field (unlike `not-in`/`!=`), so this replaces the old `followUpDate !=
// null` (which forces an implicit ascending scan ordered by followUpDate
// itself — meaning limit(100) always returned the OLDEST quotes that ever
// had a followUpDate, not the newest/most-relevant, overdue-or-due-today
// ones the badge actually needs).
const OPEN_STATUSES: QuoteStatus[] = ['Draft', 'Sent', 'Opened', 'Viewed', 'Invoiced', 'Expired', 'Converted'];

export function useFollowUpCount(enabled = true): number {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.organizationId;

  const { data: count = 0 } = useQuery({
    queryKey: ['quotes', 'followUpCount', organizationId],
    queryFn: async (): Promise<number> => {
      if (!organizationId) return 0;

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const endOfToday = new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1);

      // status and followUpDate are pushed server-side (no limit()) — the
      // candidate set is "open quotes due for follow-up on/before today",
      // which is a genuinely bounded working set (follow-ups get completed
      // and roll off), not an ever-growing collection, so an unlimited fetch
      // is safe. followUpCompletedAt is NOT filtered server-side via
      // `== null` because that field is only ever written when a follow-up
      // is completed (never initialized to null at quote creation), so a
      // `== null` where() would incorrectly exclude every quote missing the
      // field entirely. It stays a client-side falsy check instead.
      const followUpQuery = query(
        collection(db, COLLECTIONS.QUOTES),
        where('organizationId', '==', organizationId),
        where('status', 'in', OPEN_STATUSES),
        where('followUpDate', '<=', Timestamp.fromDate(endOfToday)),
        orderBy('followUpDate'),
      );

      const snapshot = await getDocs(followUpQuery);
      const quotes = snapshot.docs.map((d) => d.data() as Quote);
      return quotes.filter((q) => !q.followUpCompletedAt).length;
    },
    enabled: enabled && !!organizationId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return enabled ? count : 0;
}
